import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import _get from 'lodash/get';
import _keyBy from 'lodash/keyBy';
import classnames from 'classnames';
import { useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import RichTextWidgetIcon from 'components/Icons/RichTextWidget';
import MyTeamWidgetIcon from 'components/Icons/MyTeamWidget';
import PeopleListWidgetIcon from 'components/Icons/PeopleListWidgetIcon';
import { WidgetType } from 'pages/Homepage/typings';
import {
  RICH_TEXT_TYPE_ID,
  MY_TEAM_TYPE_ID,
  PERSONS_LISTS_BY_DATE_TYPE_ID,
} from 'pages/Homepage/helpers/consts';

interface Props {
  className?: string;
  itemName: string;
  link?: boolean;
  onClick?: (title: string) => void;
}

function WidgetTypeCard({ className, itemName, link, onClick }: Props) {
  const classes = useStyles();
  const location = useLocation();
  const widgetTypesArray: Array<WidgetType> = useSelector(state =>
    _get(state, ['homepageWidgets', 'widgetTypes']),
  );

  const widgetTypes = useMemo(() => _keyBy(widgetTypesArray, 'id'), [widgetTypesArray]);

  const widgetTypeLookup: {
    [itemName: string]: { title: string; Icon: any; description: string; link: string };
  } = {
    [RICH_TEXT_TYPE_ID]: {
      title: widgetTypes[RICH_TEXT_TYPE_ID].name,
      Icon: RichTextWidgetIcon,
      link: RICH_TEXT_TYPE_ID,
      description: _get(
        widgetTypes,
        [RICH_TEXT_TYPE_ID, 'description'],
        'Use Rich Text to display flexible and customizable text content on the homepage.',
      ),
    },
    [MY_TEAM_TYPE_ID]: {
      title: widgetTypes[MY_TEAM_TYPE_ID].name,
      Icon: MyTeamWidgetIcon,
      link: MY_TEAM_TYPE_ID,
      description: _get(
        widgetTypes,
        [MY_TEAM_TYPE_ID, 'description'],
        'Display a person’s team members, optionally including dotted line relationships.',
      ),
    },
    [PERSONS_LISTS_BY_DATE_TYPE_ID]: {
      title: widgetTypes[PERSONS_LISTS_BY_DATE_TYPE_ID].name,
      Icon: PeopleListWidgetIcon,
      link: PERSONS_LISTS_BY_DATE_TYPE_ID,
      description: _get(
        widgetTypes,
        [PERSONS_LISTS_BY_DATE_TYPE_ID, 'description'],
        'Showcase new hires, work anniversaries, birthdays, or any other date-based milestone',
      ),
    },
  };

  const item = widgetTypeLookup[itemName];
  const Icon = _get(item, 'Icon');
  const cardRootClass = classnames(classes.cardRoot, className, {
    [classes.cardRootHover]: link || onClick,
    [classes.marginRight]: link || onClick,
    [classes.clickable]: onClick,
  });

  const card = (
    <Card
      className={cardRootClass}
      data-testid={`${item.title}-card`}
      onClick={() => onClick && onClick(item.link)}
    >
      <CardContent classes={{ root: classes.content }}>
        <Typography className={classes.cardTitle} variant="h6">
          {item.title}
        </Typography>
        {Icon && <Icon className={classes.icon} data-testid={`${item.title}-icon`} />}
        <Typography className={classes.cardContent} variant="body2">
          {item.description}
        </Typography>
      </CardContent>
    </Card>
  );
  const cardLink = `${location.pathname}/${item.link}`;

  return link ? (
    <Link data-testid={`${item.title}-link`} to={cardLink}>
      {card}
    </Link>
  ) : (
    card
  );
}

const useStyles = makeStyles((theme: any) => ({
  cardRoot: {
    maxWidth: 250,
    maxHeight: 250,
  },
  cardRootHover: {
    '&:hover': {
      boxShadow:
        '0px 6px 14px -6px rgba(33, 33, 33, 0.12), 0px 10px 32px -4px rgba(33, 33, 33, 0.1)',
    },
  },
  clickable: {
    cursor: 'pointer',
  },
  content: {
    padding: 0,
    '&:last-child': {
      paddingBottom: 0,
    },
  },
  marginRight: {
    marginRight: theme.spacing(2),
  },
  cardTitle: {
    padding: theme.spacing(),
  },
  icon: {
    width: 214,
    height: 102,
  },
  cardContent: {
    padding: theme.spacing(1, 1, 1.5, 1),
    fontSize: theme.typography.caption.fontSize,
  },
}));

export default WidgetTypeCard;
